import React, { useMemo } from 'react';
import cn from 'classnames';
import { generate } from 'shortid';
import PropTypes from 'prop-types';
import { get, isEmpty } from 'lodash';
import ReactToolTip from 'react-tooltip';
import { FaRegEye, FaPencilAlt, FaTrash } from 'react-icons/fa';
import { HiSortAscending, HiDownload, HiSortDescending } from 'react-icons/hi';

import disputeIcon from 'assets/images/icons/dispute.svg';

import { createIconFromImage } from 'helper';
import { useDownloadFile } from 'partial/hooks';

import Button from './Button';

const parser = (v, path) => {
  const val = get(v, path, 0);
  if (typeof val === 'string') return val.toLowerCase();
  return val;
};

export const sortList = (list, sortBy) => {
  const sort = sortBy.split(':');
  const x = [sort[1] === 'asc' ? 1 : -1, sort[1] === 'asc' ? -1 : 1];
  const newSorted = [...list];
  newSorted.sort((a, b) =>
    parser(a, sort[0]) > parser(b, sort[0]) ? x[0] : x[1]
  );
  return newSorted;
};

export const tableAction = (label, onClick) => {
  const handleClick = (row) => (e) => {
    e.preventDefault();
    onClick(row);
  };
  return {
    id: 'action',
    label: <span className="sr-only">Action</span>,
    key: (row) => (
      <>
        <a
          href="/"
          className="text-primary-600 hover:text-primary-900"
          onClick={handleClick(row)}
        >
          {label}
        </a>
      </>
    ),
    width: 100,
  };
};

const DisputeIcon = createIconFromImage({ url: disputeIcon });

export function TableActions({
  onView,
  onEdit,
  onDelete,
  onDispute,
  viewDisabled,
  editDisabled,
  deleteDisabled,
  disputeDisabled,
  viewTooltip,
  editTooltip,
  deleteTooltip,
  disputeTooltip,
}) {
  const rowId = useMemo(generate, []);

  return (
    <div className="space-x-2 flex items-center">
      {onView && (
        <>
          <button
            type="button"
            data-for={`${rowId}_view_btn`}
            className="border border-gray-400 text-gray-400 hover:border-primary-800 hover:text-primary-800 p-2 rounded-md disabled:bg-gray-200 disabled:cursor-not-allowed"
            onClick={onView}
            data-tip={viewTooltip}
            disabled={viewDisabled}
          >
            <FaRegEye />
          </button>
          <ReactToolTip
            id={`${rowId}_view_btn`}
            place="bottom"
            effect="solid"
          />
        </>
      )}
      {onEdit && (
        <>
          <button
            type="button"
            data-for={`${rowId}_edit_btn`}
            className="border border-gray-400 text-gray-400 hover:border-blue-500 hover:text-blue-500 p-2 rounded-md disabled:bg-gray-200 disabled:cursor-not-allowed"
            onClick={onEdit}
            data-tip={editTooltip}
            disabled={editDisabled}
          >
            <FaPencilAlt />
          </button>
          <ReactToolTip
            id={`${rowId}_edit_btn`}
            place="bottom"
            effect="solid"
          />
        </>
      )}
      {onDelete && (
        <>
          <button
            type="button"
            data-for={`${rowId}_delete_btn`}
            className="border border-gray-400 text-gray-400 hover:border-red-500 hover:text-red-500 p-2 rounded-md disabled:bg-gray-200 disabled:cursor-not-allowed"
            onClick={onDelete}
            data-tip={deleteTooltip}
            disabled={deleteDisabled}
          >
            <FaTrash />
          </button>
          <ReactToolTip
            id={`${rowId}_delete_btn`}
            place="bottom"
            effect="solid"
          />
        </>
      )}
      {onDispute && (
        <>
          <button
            type="button"
            data-for={`${rowId}_dispute_btn`}
            className="border border-gray-400 text-gray-400 hover:border-primary-800 hover:text-primary-800 p-2 rounded-md disabled:bg-gray-200 disabled:cursor-not-allowed"
            onClick={onDispute}
            disabled={disputeDisabled}
            data-tip={disputeTooltip}
          >
            <DisputeIcon className="w-4 h-4 max-w-none" />
          </button>
          <ReactToolTip
            id={`${rowId}_dispute_btn`}
            place={disputeTooltip?.length > 15 ? 'left' : 'bottom'}
            effect="solid"
          />
        </>
      )}
    </div>
  );
}

TableActions.defaultProps = {
  onView: null,
  onEdit: null,
  onDelete: null,
  onDispute: null,
  viewDisabled: false,
  editDisabled: false,
  deleteDisabled: false,
  disputeDisabled: false,
  viewTooltip: 'View',
  editTooltip: 'Edit',
  deleteTooltip: 'Delete',
  disputeTooltip: 'Dispute',
};

TableActions.propTypes = {
  onView: PropTypes.oneOfType([PropTypes.func, PropTypes.exact(null)]),
  onEdit: PropTypes.oneOfType([PropTypes.func, PropTypes.exact(null)]),
  onDelete: PropTypes.oneOfType([PropTypes.func, PropTypes.exact(null)]),
  onDispute: PropTypes.oneOfType([PropTypes.func, PropTypes.exact(null)]),
  viewDisabled: PropTypes.bool,
  editDisabled: PropTypes.bool,
  deleteDisabled: PropTypes.bool,
  disputeDisabled: PropTypes.bool,
  viewTooltip: PropTypes.string,
  editTooltip: PropTypes.string,
  deleteTooltip: PropTypes.string,
  disputeTooltip: PropTypes.string,
};

export const tableEditDelete = (onView, onEdit, onDelete) => {
  const handleViewClick = (row) => (e) => {
    e.preventDefault();
    onView(row);
  };
  const handleEditClick = (row) => (e) => {
    e.preventDefault();
    onEdit(row);
  };
  const handleDeleteClick = (row) => (e) => {
    e.preventDefault();
    onDelete(row);
  };
  return {
    id: 'action',
    label: 'Action',
    key: (row) => (
      <div className="space-x-2 flex items-center">
        {onView && (
          <>
            <button
              type="button"
              className="border border-gray-400 text-gray-400 hover:border-primary-800 hover:text-primary-800 p-2 rounded-md"
              onClick={handleViewClick(row)}
              data-tip="View"
            >
              <FaRegEye />
            </button>
            <ReactToolTip effect="solid" place="bottom" />
          </>
        )}
        {onEdit && (
          <>
            <button
              type="button"
              className="border border-gray-400 text-gray-400 hover:border-blue-500 hover:text-blue-500 p-2 rounded-md"
              onClick={handleEditClick(row)}
              data-tip="Edit"
            >
              <FaPencilAlt />
            </button>
            <ReactToolTip effect="solid" place="bottom" />
          </>
        )}
        {onDelete && (
          <>
            <button
              type="button"
              className="border border-gray-400 text-gray-400 hover:border-red-500 hover:text-red-500 p-2 rounded-md"
              onClick={handleDeleteClick(row)}
              data-tip="Delete"
            >
              <FaTrash />
            </button>
            <ReactToolTip effect="solid" place="bottom" />
          </>
        )}
      </div>
    ),
    width: 100,
  };
};

function Table({
  format,
  data,
  downloadCsvUrl,
  downloadFilename,
  isLoading,
  subLabel,
  sortKey,
  onSort,
  sort,
}) {
  const [downloadFile, isDownloadFileLoading] = useDownloadFile({
    downloadUrl: downloadCsvUrl,
    fileName: downloadFilename,
  });
  const [sortBy, orderBy] = React.useMemo(() => (sort || '').split(':'), [
    sort,
  ]);
  const handleDownload = () => {
    downloadFile();
  };
  const handleSort = (col) => (e) => {
    e.preventDefault();
    const switchOrder = orderBy === 'asc' ? 'desc' : 'asc';
    onSort((state) => ({
      ...state,
      [sortKey]: `${col.sortKey}:${
        sortBy !== col.sortKey ? 'asc' : switchOrder
      }`,
    }));
  };
  return (
    <div className="flex flex-col flex-grow rounded-lg overflow-hidden">
      <div className="relative flex flex-grow isolate">
        <div className="overflow-auto absolute h-full w-full">
          {isLoading || data.length < 1 ? (
            <>
              {isLoading ? (
                <div className="grid grid-cols-2 gap-3 p-3">
                  <div className="animate-pulse bg-gray-200 h-8 w-full rounded" />
                  <div className="animate-pulse bg-gray-200 h-8 w-full rounded" />
                  <div className="col-span-2 animate-pulse bg-gray-200 h-8 w-full rounded" />
                  <div className="col-span-2 animate-pulse bg-gray-200 h-8 w-full rounded" />
                  <div className="col-span-2 animate-pulse bg-gray-200 h-8 w-full rounded" />
                </div>
              ) : (
                <div className="text-center pt-5">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      vectorEffect="non-scaling-stroke"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
                    />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    Empty
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    There are no items.
                  </p>
                </div>
              )}
            </>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="sticky top-0 bg-white z-10">
                <tr>
                  {React.Children.toArray(
                    format.map((col) => {
                      return (
                        <th scope="col" width={col.width || 'initial'}>
                          {onSort && col.sortKey ? (
                            <a
                              href="/"
                              onClick={handleSort(col)}
                              className={cn(
                                'whitespace-nowrap px-6 py-4 text-left font-medium tracking-wider flex items-center space-x-2 hover:bg-gray-100 transition',
                                sortBy === col.sortKey
                                  ? 'text-primary-500'
                                  : 'text-gray-500'
                              )}
                            >
                              <span>{col.label}</span>
                              {sortBy === col.sortKey && orderBy === 'desc' ? (
                                <HiSortAscending className="w-4 h-4" />
                              ) : (
                                <HiSortDescending className="w-4 h-4" />
                              )}
                            </a>
                          ) : (
                            <span className="px-6 py-4 text-left font-medium tracking-wider flex items-center space-x-2">
                              {col.label}
                            </span>
                          )}
                        </th>
                      );
                    })
                  )}
                </tr>
              </thead>
              <tbody>
                {React.Children.toArray(
                  data.map((row) => (
                    <tr
                      // className={cn("group cursor-pointer hover:bg-gray-50", index % 2 === 0 ? 'bg-white' : 'bg-gray-50')}
                      className={cn(
                        'group cursor-pointer border-t border-b hover:bg-gray-50 border-gray-100 transition'
                      )}
                    >
                      {React.Children.toArray(
                        format.map((col) => {
                          const content = (() => {
                            if (typeof col.key === 'function')
                              return col.key(row);
                            return get(row, col.key);
                          })();
                          return (
                            <td
                              className={cn(
                                'px-6 py-4 text-left text-base',
                                col.className || ''
                              )}
                              width={col.width || 'initial'}
                            >
                              {content}
                            </td>
                          );
                        })
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {(downloadCsvUrl || subLabel) && (
        <div className="bg-gray-50 flex items-center justify-between">
          {downloadCsvUrl && !isEmpty(data) ? (
            <div className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <Button
                primary
                icon={HiDownload}
                onClick={handleDownload}
                isLoading={isDownloadFileLoading}
              >
                Download CSV
              </Button>
            </div>
          ) : (
            <span />
          )}
          {subLabel ? (
            <div className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {subLabel}
            </div>
          ) : (
            <span />
          )}
        </div>
      )}
    </div>
  );
}

Table.defaultProps = {
  downloadFilename: 'download.csv',
  downloadCsvUrl: '',
  isLoading: false,
  sortKey: 'sort',
  onSort: false,
  subLabel: '',
  sort: '',
  data: [],
};

Table.propTypes = {
  onSort: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  format: PropTypes.instanceOf(Array).isRequired,
  data: PropTypes.instanceOf(Array),
  downloadFilename: PropTypes.string,
  downloadCsvUrl: PropTypes.string,
  subLabel: PropTypes.string,
  sortKey: PropTypes.string,
  isLoading: PropTypes.bool,
  sort: PropTypes.string,
};

export default Table;
